import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { IKUserMatch, IPKUserMatch } from '../../pk';
import { AppTextService } from '../../../../shared/app-text';
import { PkMatchType } from '../../pk-match-type';
import { FiProfileSizes } from '../../../../shared/profile-icon/profile-icon.component';
import { getObjectTypeNameTextId, isDefined } from '../../../../shared/utils';
import { UserModel } from '../../../../user/user.model';
import { ObjectTypes } from '@fi-shared/types';
import { IContactModel } from '@fi-shared/models/common';
import { FiContactsService } from '@fi-shared/fi-contacts/fi-contacts.service';
import { PKUsersImportType } from '@fi-app/external-platform-integration/pk/pk-users-import-type';
import { PkHelperService } from '@fi-app/external-platform-integration/pk/pk-helper.service';

@Component({
    selector: 'fi-pk-users-matching-table',
    templateUrl: './fi-pk-users-matching-table.component.html',
    styleUrls: ['./fi-pk-users-matching-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-pk-users-matching-table' },
    standalone: false,
})
export class FiPkUsersMatchingTableComponent implements OnInit {
    @Input() cardTitle: string = '';
    @Input() cardSubtitle: string = '';
    @Input() pkUsers: IPKUserMatch[] = [];
    @Input() chainformationUsers: UserModel[] = [];
    @Input() importTableType: PkMatchType;
    @Input() usersImportType: PKUsersImportType;
    @Output() onUserClick: EventEmitter<IKUserMatch> = new EventEmitter<IKUserMatch>();
    profilePictureSize = FiProfileSizes;
    readonly unitRoleDisplayLimit: number = 2;

    constructor(
        private pkHelperService: PkHelperService,
        private appTextService: AppTextService,
        private fiContactsService: FiContactsService,
    ) {}

    ngOnInit(): void {}

    @HostBinding('class.hidden')
    get isHidden() {
        return !this.chainformationUsers.length;
    }

    public getCardTitle(): string {
        const isSingleUser: boolean = this.chainformationUsers.length === 1;
        return `${this.cardTitle} - ${this.getUsersCount()} ${this.appTextService
            .get(isSingleUser ? 'user.user' : 'common.users')
            .toLowerCase()}`;
    }
    private getUsersCount(): number {
        if (this.importTableType === PkMatchType.NotMatched)
            return this.chainformationUsers.filter((user: UserModel) => !isDefined(this.getMatchedPkUser(user))).length;

        return this.chainformationUsers.length;
    }

    public getMatCardClass(): string {
        return PkMatchType[this.importTableType];
    }

    public onRowItemClick(user: UserModel): void {
        this.onUserClick.emit({ chainformationUser: user, pkUser: this.getMatchedPkUser(user) });
    }

    private getChainformationUserEmail(user: UserModel): string {
        return user.EmailAddressContacts.length && user.EmailAddressContacts[0].ContactValue
            ? user.EmailAddressContacts[0].ContactValue
            : '';
    }

    private getChainformationUserPhoneNumber(user: UserModel): string {
        return user.PhoneNumberContacts.length && user.PhoneNumberContacts[0].ContactValue
            ? this.fiContactsService.getFullContactValue(user.PhoneNumberContacts[0])
            : '';
    }

    getChainformationUserContactValue(user: UserModel): string {
        if (this.usersImportType === PKUsersImportType.ByEmail) return this.getChainformationUserEmail(user);

        if (this.usersImportType === PKUsersImportType.ByPhoneNumber)
            return this.getChainformationUserPhoneNumber(user);
    }

    public getUserContactValue(user: UserModel): string {
        const matchedPkUser = this.getMatchedPkUser(user);
        return isDefined(matchedPkUser)
            ? this.pkHelperService.getUserContactValue(matchedPkUser, this.usersImportType)
            : '';
    }

    public getPkUserFullName(user: UserModel): string {
        const matchedPkUser: IPKUserMatch = this.getMatchedPkUser(user);
        return isDefined(matchedPkUser) ? `${matchedPkUser.FirstName} ${matchedPkUser.LastName}` : '';
    }

    showUnitsTooltip(user: UserModel) {
        const pkUnitNames = this.getPkUnitNames(user);
        return pkUnitNames.length > this.unitRoleDisplayLimit;
    }

    getPKUserUnitNamesText(user: UserModel, fullMode?: boolean): string {
        const pkUnitNames = this.getPkUnitNames(user);
        if (!fullMode) {
            return pkUnitNames.length > this.unitRoleDisplayLimit ? pkUnitNames[0] : pkUnitNames.join(', ');
        }

        return pkUnitNames.slice(1).join('<br>');
    }

    getTooltipButtonText(user: UserModel): string {
        const pkUnitNames = this.getPkUnitNames(user);
        const itemsAmount = pkUnitNames.length;
        return ` + ${itemsAmount - 1} ${this.appTextService.get(
            getObjectTypeNameTextId(ObjectTypes.BusinessUnit, itemsAmount - 1 !== 1),
        )}`;
    }

    private getPkUnitNames(user: UserModel): string[] {
        const pkUserMatch = this.getMatchedPkUser(user);
        return pkUserMatch ? pkUserMatch.UnitNames : [];
    }

    private getMatchedPkUser(user: UserModel): IPKUserMatch {
        return this.pkUsers.find((pkUser: IPKUserMatch) => pkUser.UserId === user.Id);
    }

    getUserActiveText(user: UserModel) {
        return user.IsUserActive ? '' : this.appTextService.get('common.deactivated');
    }
}
